import React, { useEffect, useState } from "react";
import { SearchInput } from "@src/components/SearchInput";
import { useForm } from "react-hook-form";
import { theme } from "@src/theme";
import styled from "styled-components";
import { ChevronDown, X } from "lucide-react";
import Spinner from "../Spinner";

const Container = styled.div`
  color: #fff;
  padding-bottom: 20px;
  width: 100%;
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.div<{ $hasValue: boolean }>`
  border-bottom: 1px solid ${theme.colors.yellow};
  padding: 8px 0px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ $hasValue }) =>
    $hasValue ? theme.colors.white : theme.colors.gray};
`;

const SelectValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: ${theme.colors.darkBg};
  border: 1px solid ${theme.colors.yellow};
  border-radius: 4px;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  margin-top: 4px;
`;

const OptionItem = styled.div<{ $isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  color: ${({ $isSelected }) =>
    $isSelected ? theme.colors.yellow : theme.colors.gray};
  background-color: ${({ $isSelected }) =>
    $isSelected ? theme.colors.gray300 : "transparent"};
  &:hover {
    background-color: ${theme.colors.gray300};
    color: ${theme.colors.yellow};
  }
`;

const SearchContainer = styled.div`
  padding: ${theme.margins.medium};
  border-bottom: 1px solid ${theme.colors.yellow};
`;

const EmptyMessage = styled.div`
  padding: ${theme.margins.medium};
  color: ${theme.colors.gray};
  text-align: center;
  font-style: italic;
`;

type SingleSelectWithInfiniteScrollProps<T> = {
  options: T[];
  selected: T | null;
  onSelect: (option: T | null) => void;
  labelKey: keyof T;
  valueKey: keyof T;
  placeholder: string;
  loading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  loaderRef?: React.RefObject<HTMLElement>;
  searchText?: string;
  onSearchTextChange?: (text: string) => void;
  disabled?: boolean;
  disabledTitle?: string;
};

function SingleSelectWithInfiniteScroll<T extends { [key: string]: any }>({
  options,
  selected,
  onSelect,
  labelKey,
  valueKey,
  placeholder,
  loading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  loaderRef,
  searchText: externalSearchText,
  onSearchTextChange,
  disabled = false,
  disabledTitle,
}: SingleSelectWithInfiniteScrollProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const { register, watch, setValue } = useForm({
    defaultValues: { searchText: "" },
  });
  const internalSearchText = watch("searchText");

  // Update external search text when internal changes
  useEffect(() => {
    if (onSearchTextChange && externalSearchText === undefined) {
      const debounceTimer = setTimeout(() => {
        onSearchTextChange(internalSearchText);
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [internalSearchText, onSearchTextChange, externalSearchText]);

  // Sync external search text to form
  useEffect(() => {
    if (externalSearchText !== undefined) {
      setValue("searchText", externalSearchText);
    }
  }, [externalSearchText, setValue]);

  // Use options directly since filtering is done on the backend
  const filteredOptions = options;

  const handleSelect = (option: T) => {
    const isSelected = selected && selected[valueKey] === option[valueKey];
    onSelect(isSelected ? null : option);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(null);
  };

  const selectedLabel = disabled
    ? (disabledTitle ?? placeholder)
    : selected
      ? String(selected[labelKey] || "")
      : placeholder;

  useEffect(() => {
    if (!isOpen) return;

    let observer: IntersectionObserver | null = null;

    const timeoutId = setTimeout(() => {
      if (!loaderRef?.current || !hasNextPage || isFetchingNextPage) return;

      observer = new IntersectionObserver(
        (entries) => {
          const target = entries[0];
          if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
            onLoadMore?.();
          }
        },
        {
          root: null,
          rootMargin: "200px",
          threshold: 0.1,
        }
      );

      const currentRef = loaderRef.current;
      observer.observe(currentRef);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observer && loaderRef?.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [isOpen, hasNextPage, isFetchingNextPage, loaderRef, onLoadMore]);

  return (
    <Container
      style={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
      title={disabled ? disabledTitle : undefined}
    >
      <SelectWrapper>
        <SelectButton
          $hasValue={!!selected && !disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <SelectValue>{selectedLabel}</SelectValue>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {selected && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                aria-label="Clear selection"
              >
                <X size={16} color={theme.colors.yellow} />
              </button>
            )}
            <ChevronDown
              size={16}
              color={theme.colors.yellow}
              style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </div>
        </SelectButton>
        <DropdownMenu $isOpen={isOpen}>
          <SearchContainer>
            <SearchInput
              placeholder="Search..."
              {...register("searchText")}
              onClick={(e) => e.stopPropagation()}
            />
          </SearchContainer>
          {loading && filteredOptions.length === 0 ? (
            <EmptyMessage>
              <Spinner small />
            </EmptyMessage>
          ) : filteredOptions.length === 0 ? (
            <EmptyMessage>No options found</EmptyMessage>
          ) : (
            <>
              {filteredOptions.map((option) => {
                const isSelected =
                  selected && selected[valueKey] === option[valueKey];
                return (
                  <OptionItem
                    key={String(option[valueKey])}
                    $isSelected={!!isSelected}
                    onClick={() => handleSelect(option)}
                  >
                    {String(option[labelKey])}
                  </OptionItem>
                );
              })}
              {loaderRef && (
                <div
                  ref={loaderRef as React.RefObject<HTMLDivElement>}
                  style={{ height: 1, width: "100%" }}
                />
              )}
              {isFetchingNextPage && (
                <div
                  style={{ padding: theme.margins.medium, textAlign: "center" }}
                >
                  <Spinner small />
                </div>
              )}
            </>
          )}
        </DropdownMenu>
      </SelectWrapper>
    </Container>
  );
}

export default SingleSelectWithInfiniteScroll;
